import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaInstagram, FaFacebook, FaYoutube, FaTree } from "react-icons/fa";
import { supabase } from "../config";
import TreeCounter from "../components/TreeCounter";
import PaymentInfo from "../components/PaymentInfo";

const GOAL = 50000;

const TIERS = [
  { trees: 10, usd: 18, inr: 1500 },
  { trees: 50, usd: 90, inr: 7500 },
  { trees: 100, usd: 180, inr: 15000 },
];

function Landing() {
  const [paidTrees, setPaidTrees] = useState(0);
  const [totalTrees, setTotalTrees] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCounts() {
      try {
        // Total trees (all contributions)
        const { data: totalData } = await supabase
          .from("sritaru_contributions")
          .select("num_trees");

        // Planted trees
        const { data: paidData } = await supabase
          .from("sritaru_contributions")
          .select("num_trees")
          .in("payment_status", ["planted", "paid", "zelle_paid", "gpay_paid"]);

        const total = (totalData || []).reduce(
          (sum, r) => sum + (r.num_trees || 0),
          0
        );
        const paid = (paidData || []).reduce(
          (sum, r) => sum + (r.num_trees || 0),
          0
        );

        setTotalTrees(total);
        setPaidTrees(paid);
      } catch (err) {
        console.error("Error fetching tree counts:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchCounts();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sritaru-700 via-sritaru-600 to-sritaru-800 text-white py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-krushna text-3xl sm:text-5xl mb-3 leading-tight">
            శ్రీతరు సంకల్పం 2026
          </h1>
          <h2 className="text-2xl sm:text-4xl font-bold mb-4">
            SriTaru Sankalpam
          </h2>
          <p className="text-lg sm:text-xl text-sritaru-100 mb-2">
            Plant Trees, Transform Lives
          </p>
          <p className="text-sm sm:text-base text-sritaru-200 mb-8 font-krushna">
            An Agroforesting Initiative | రైతుల భూముల్లో చెట్లు నాటుదాం
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl py-4 px-6 inline-block mb-8">
            <p className="text-2xl sm:text-3xl font-bold">
              $1.80 per tree | &#8377;150 per tree
            </p>
          </div>

          <div className="mb-8">
            <Link
              to="/contribute"
              className="inline-block bg-earth-400 hover:bg-earth-500 text-white font-bold text-lg px-10 py-4 rounded-xl shadow-lg transition-colors"
            >
              &#127795; Contribute Now
            </Link>
          </div>

          {!loading && (
            <TreeCounter
              paidTrees={paidTrees}
              totalTrees={totalTrees}
              goal={GOAL}
            />
          )}
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-sritaru-800 text-center mb-10">
            Choose Your Impact
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TIERS.map((tier) => (
              <div
                key={tier.trees}
                className="bg-sritaru-50 border-2 border-sritaru-200 rounded-xl p-6 text-center hover:border-sritaru-500 hover:shadow-lg transition-all"
              >
                <div className="text-4xl mb-2">
                  <FaTree className="inline text-sritaru-500" />
                </div>
                <p className="text-3xl font-bold text-sritaru-700">
                  {tier.trees}
                </p>
                <p className="text-gray-600 mb-3">trees</p>
                <p className="text-lg font-semibold text-sritaru-800">
                  ${tier.usd}
                </p>
                <p className="text-sm text-gray-500">
                  &#8377;{tier.inr.toLocaleString()}
                </p>
              </div>
            ))}
            <div className="bg-earth-50 border-2 border-earth-200 rounded-xl p-6 text-center hover:border-earth-500 hover:shadow-lg transition-all">
              <div className="text-4xl mb-2">&#10024;</div>
              <p className="text-2xl font-bold text-earth-600">Custom</p>
              <p className="text-gray-600 mb-3">amount</p>
              <p className="text-sm text-gray-500">
                Choose any number of trees
              </p>
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            To be planted in Farmers Lands in AP and Telangana. Benefits going
            to Farmers.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-sritaru-50">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-sritaru-800 text-center mb-10">
            How It Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Choose & Pay",
                desc: "Select the number of trees and make a payment via Zelle (USA) or G-Pay (India).",
                icon: "&#128176;",
              },
              {
                step: "2",
                title: "Register",
                desc: "Fill out the contribution form with your details and dedication.",
                icon: "&#128221;",
              },
              {
                step: "3",
                title: "Trees Get Planted",
                desc: "SankalpTaru plants trees in farmers' lands. Track them with geo-tagged photos!",
                icon: "&#127795;",
              },
            ].map((item) => (
              <div
                key={item.step}
                className="bg-white rounded-xl shadow-md p-6 text-center"
              >
                <div
                  className="text-4xl mb-3"
                  dangerouslySetInnerHTML={{ __html: item.icon }}
                />
                <div className="inline-block bg-sritaru-600 text-white text-sm font-bold w-8 h-8 rounded-full leading-8 mb-3">
                  {item.step}
                </div>
                <h4 className="text-lg font-bold text-sritaru-700 mb-2">
                  {item.title}
                </h4>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Info */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-sritaru-800 text-center mb-10">
            Payment Options
          </h3>
          <PaymentInfo />
        </div>
      </section>

      {/* Links / Media */}
      <section className="py-16 px-4 bg-sritaru-50">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-sritaru-800 mb-6">
            Watch & Follow
          </h3>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a
              href="https://www.instagram.com/reel/DS_H2GuDhFj/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-5 py-3 rounded-lg hover:opacity-90 transition-opacity"
            >
              <FaInstagram size={20} /> Instagram
            </a>
            <a
              href="https://www.facebook.com/share/v/1CuPDzwmBX/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-blue-600 text-white px-5 py-3 rounded-lg hover:opacity-90 transition-opacity"
            >
              <FaFacebook size={20} /> Facebook
            </a>
            <a
              href="https://www.youtube.com/watch?v=eCJxRJQpG4U"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-red-600 text-white px-5 py-3 rounded-lg hover:opacity-90 transition-opacity"
            >
              <FaYoutube size={20} /> YouTube
            </a>
          </div>
          <a
            href="https://sankalptaru.org/c/bpluswithbhaskar/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-sritaru-600 text-white px-8 py-3 rounded-lg hover:bg-sritaru-700 transition-colors font-semibold"
          >
            &#127795; View Geo-Tagged Trees on SankalpTaru
          </a>
        </div>
      </section>
    </div>
  );
}

export default Landing;
